<script setup>
import { ref, watch, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import OrderIssueItem from './OrderIssueItem.vue';
import OrderIssueModal from 'dashboard/routes/dashboard/orderIssues/OrderIssueModal.vue';
import orderIssuesAPI from 'dashboard/api/orderIssues';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';

const props = defineProps({
  contactId: {
    type: [Number, String],
    required: true,
  },
  conversationId: {
    type: [Number, String],
    default: null,
  },
});

const store = useStore();

const issues = ref([]);
const loading = ref(true);
const error = ref('');
const showCreateModal = ref(false);

// Get current conversation to watch for status changes
const currentConversation = computed(() => 
  store.getters.getSelectedChat || {}
);

const conversationDisplayId = computed(() => 
  currentConversation.value?.display_id
);

const fetchIssues = async () => {
  if (!props.contactId) {
    loading.value = false;
    return;
  }
  
  try {
    loading.value = true;
    error.value = '';
    const response = await orderIssuesAPI.getByContact(props.contactId);
    issues.value = response.data || [];
  } catch (e) {
    error.value = e.response?.data?.message || 'CONVERSATION_SIDEBAR.ORDER_ISSUES.FETCH_ERROR';
    issues.value = [];
  } finally {
    loading.value = false;
  }
};

watch(
  () => props.contactId,
  () => fetchIssues(),
  { immediate: true }
);

// Watch conversation status changes to auto-refresh order issues
watch(
  () => currentConversation.value?.status,
  (newStatus, oldStatus) => {
    if (oldStatus !== undefined && newStatus !== oldStatus) {
      // Refresh when status changes (resolved/open)
      fetchIssues();
    }
  }
);

const handleCreateSuccess = () => {
  showCreateModal.value = false;
  fetchIssues();
  // Notify other components (popover) to refresh
  emitter.emit(BUS_EVENTS.ORDER_ISSUES_UPDATED, { contactId: props.contactId });
};

// Handler for updates from popover
const handleIssueUpdated = () => {
  fetchIssues();
  // Notify other components (popover) to refresh
  emitter.emit(BUS_EVENTS.ORDER_ISSUES_UPDATED, { contactId: props.contactId });
};

// Listen for updates from other components (popover)
const onOrderIssuesUpdated = (data) => {
  // Only refresh if it's for the same contact
  if (data?.contactId === props.contactId) {
    fetchIssues();
  }
};

onMounted(() => {
  emitter.on(BUS_EVENTS.ORDER_ISSUES_UPDATED, onOrderIssuesUpdated);
});

onUnmounted(() => {
  emitter.off(BUS_EVENTS.ORDER_ISSUES_UPDATED, onOrderIssuesUpdated);
});
</script>

<template>
  <div class="px-4 py-2 text-n-slate-12">
    <div v-if="loading" class="flex justify-center items-center p-4">
      <Spinner size="32" class="text-n-brand" />
    </div>
    <div v-else-if="error" class="text-center text-n-ruby-12">
      {{ $t(error) }}
    </div>
    <template v-else>
      <!-- No issues message -->
      <p v-if="!issues.length" class="text-sm text-n-slate-11 text-center py-2">
        {{ $t('CONVERSATION_SIDEBAR.ORDER_ISSUES.NO_ISSUES') }}
      </p>
      
      <!-- Existing issues list -->
      <div v-if="issues.length">
        <OrderIssueItem
          v-for="issue in issues"
          :key="issue.id"
          :issue="issue"
          @updated="handleIssueUpdated"
        />
      </div>
      
      <!-- Always show create button -->
      <div class="flex justify-center pt-2">
        <button
          class="text-xs px-3 py-1.5 rounded bg-n-alpha-2 hover:bg-n-alpha-3 text-n-slate-12 transition-colors flex items-center gap-1"
          @click="showCreateModal = true"
        >
          <i class="i-lucide-plus text-sm" />
          {{ issues.length 
            ? $t('CONVERSATION_SIDEBAR.ORDER_ISSUES.LOG_ANOTHER') 
            : $t('CONVERSATION_SIDEBAR.ORDER_ISSUES.CREATE') 
          }}
        </button>
      </div>
    </template>

    <!-- Create Order Issue Modal - createMode forces new issue creation -->
    <OrderIssueModal
      :show="showCreateModal"
      :conversation-id="conversationId"
      :conversation-display-id="conversationDisplayId"
      :create-mode="true"
      @close="showCreateModal = false"
      @success="handleCreateSuccess"
    />
  </div>
</template>
